import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Digitisation client — talks DIRECTLY to the services host, same as
 * bridge-frontend's document dashboard. The api-lab-investigation-v2-
 * service no longer proxies these calls (routes removed on the backend)
 * — clinicians already have valid session tokens that the direct
 * upstream accepts, so the proxy hop was pure overhead.
 *
 *   listDocuments(patientId) — GET /v1/documents/digitisation/:patientId
 *     Upstream returns a raw array `[{document: {id, title, url,
 *     thumbnail, ...}, appointment, service}, …]`. We flatten
 *     `document.*` into a picker-friendly `{id, name, url, thumbnail,
 *     type, uploaded}` shape.
 *
 *   digitise(patientId, documentId) — POST /v1/lab-report-digitisation/
 *     digitise-report body `{documentId: N}`. Digitisation is ASYNC
 *     upstream ("check back in 10 minutes" per bridge); on any 2xx we
 *     resolve as success and let the caller refresh. Best-effort count
 *     parse from the response body.
 */

// Normalise one nested list row into a flat picker row. Skips rows with
// no id — anything else is best-effort.
const flattenDoc = (row) => {
  if (!row || typeof row !== "object") return null;
  const d = row.document || row;
  const id = d && d.id != null ? String(d.id) : "";
  if (!id) return null;
  return {
    id,
    name: d.title || d.description || d.name || "",
    url: d.url || "",
    thumbnail: d.thumbnail || "",
    type: d.typeTitle || d.type || "",
    uploaded: d.createdAt || d.updatedAt || "",
  };
};

const parseCount = (res) => {
  if (!res || typeof res !== "object") return null;
  for (const k of ["count", "paramCount", "valuesCount", "valuesAdded", "digitisedParamCount"]) {
    if (typeof res[k] === "number") return res[k];
  }
  return null;
};

class DigitisationService {
  constructor() {
    this.apiService = new APIService();
  }

  listDocuments(patientId, onResponse, onError) {
    const url = APIConfig.digitisation.documentsForDigitisation.format(patientId);
    return this.apiService.get(
      url,
      (res) => {
        // Upstream returns a raw array (bridge relies on this). Accept
        // `{data: [...]}` as a defensive alternative in case the shape
        // ever wraps.
        const rows = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
        onResponse(rows.map(flattenDoc).filter(Boolean));
      },
      (err) => onError && onError(err)
    );
  }

  /**
   * Signed URL token used to view documents in a new tab. The file host
   * returns 403 on direct hits — the token needs to be appended as a
   * `?token=…` query string. Bridge caches this in localStorage under
   * `zy.bridge.doc.token`; we reuse the same key so one fetch warms
   * both apps. Falls back to an empty string on failure — callers will
   * hit the 403 they'd have hit anyway.
   */
  getDocumentToken(onResponse, onError) {
    const cachedRaw = (() => {
      try { return localStorage.getItem("zy.bridge.doc.token"); }
      catch (e) { return null; }
    })();
    if (cachedRaw) {
      try {
        const parsed = JSON.parse(cachedRaw);
        if (parsed && parsed.token) {
          onResponse(parsed.token);
          return;
        }
      } catch (e) { /* fall through to refetch */ }
    }
    const url = APIConfig.digitisation.documentToken;
    return this.apiService.get(
      url,
      (res) => {
        const token = (res && res.token) || "";
        if (token) {
          try {
            localStorage.setItem("zy.bridge.doc.token", JSON.stringify(res));
          } catch (e) { /* private mode etc — token still usable this session */ }
        }
        onResponse(token);
      },
      (err) => onError && onError(err)
    );
  }

  digitise(patientId, documentId, onResponse, onError) {
    const url = APIConfig.digitisation.triggerDigitisation;
    const idNum = parseInt(documentId, 10);
    if (!idNum) {
      onError && onError({ message: "documentId must be numeric" });
      return;
    }
    return this.apiService.post(
      url,
      { documentId: idNum },
      (res) => {
        onResponse({
          documentId: String(idNum),
          success: true,
          message: (res && res.message) || "Digitisation started",
          count: parseCount(res),
        });
      },
      (err) => {
        // Preserve the message for the modal's inline error strip.
        onError && onError(err);
      }
    );
  }
}

export default DigitisationService;
